import React, { createContext, useContext, useState, useEffect } from 'react';

// Exchange rates (in a real app, these would come from an API)
const EXCHANGE_RATES = {
    EUR: 1.0,     // Base currency
    USD: 1.09,    // US Dollar
    AUD: 1.65,    // Australian Dollar
    CAD: 1.50,    // Canadian Dollar
    CZK: 25.5,    // Czech Koruna
    HUF: 390.0,   // Hungarian Forint
    JPY: 160.0,   // Japanese Yen
    KZT: 480.0,   // Kazakhstani Tenge
    NOK: 11.8,    // Norwegian Krone
    AZN: 1.85,    // Azerbaijani Manat
    UAH: 41.0,    // Ukrainian Hryvnia
    PLN: 4.35,    // Polish Zloty
    UZS: 12800.0, // Uzbekistani Som
    CLP: 950.0,   // Chilean Peso
    IDR: 16200.0, // Indonesian Rupiah
    NZD: 1.75,    // New Zealand Dollar
    PEN: 3.80,    // Peruvian Sol
    BHD: 0.41,    // Bahraini Dinar
    JOD: 0.77,    // Jordanian Dinar
    KWD: 0.33,    // Kuwaiti Dinar
    OMR: 0.42,    // Omani Rial
    QAR: 3.95,    // Qatari Riyal
    SAR: 4.08,    // Saudi Riyal
    AED: 3.99,    // UAE Dirham
};

// Currency symbols and names
const CURRENCY_INFO = {
    EUR: { symbol: '€', name: 'Euro', decimals: 2 },
    USD: { symbol: '$', name: 'US Dollar', decimals: 2 },
    AUD: { symbol: 'A$', name: 'Australian Dollar', decimals: 2 },
    CAD: { symbol: 'C$', name: 'Canadian Dollar', decimals: 2 },
    CZK: { symbol: 'Kč', name: 'Czech Koruna', decimals: 2 },
    HUF: { symbol: 'Ft', name: 'Hungarian Forint', decimals: 0 },
    JPY: { symbol: '¥', name: 'Japanese Yen', decimals: 0 },
    KZT: { symbol: '₸', name: 'Kazakhstani Tenge', decimals: 2 },
    NOK: { symbol: 'kr', name: 'Norwegian Krone', decimals: 2 },
    AZN: { symbol: '₼', name: 'Azerbaijani Manat', decimals: 2 },
    UAH: { symbol: '₴', name: 'Ukrainian Hryvnia', decimals: 2 },
    PLN: { symbol: 'zł', name: 'Polish Zloty', decimals: 2 },
    UZS: { symbol: 'soʻm', name: 'Uzbekistani Som', decimals: 0 },
    CLP: { symbol: '$', name: 'Chilean Peso', decimals: 0 },
    IDR: { symbol: 'Rp', name: 'Indonesian Rupiah', decimals: 0 },
    NZD: { symbol: 'NZ$', name: 'New Zealand Dollar', decimals: 2 },
    PEN: { symbol: 'S/', name: 'Peruvian Sol', decimals: 2 },
    BHD: { symbol: 'BD', name: 'Bahraini Dinar', decimals: 3 },
    JOD: { symbol: 'JD', name: 'Jordanian Dinar', decimals: 3 },
    KWD: { symbol: 'KD', name: 'Kuwaiti Dinar', decimals: 3 },
    OMR: { symbol: 'ر.ع.', name: 'Omani Rial', decimals: 3 },
    QAR: { symbol: 'QR', name: 'Qatari Riyal', decimals: 2 },
    SAR: { symbol: 'SR', name: 'Saudi Riyal', decimals: 2 },
    AED: { symbol: 'AED', name: 'UAE Dirham', decimals: 2 },
};

const CurrencyContext = createContext();

export const useCurrency = () => {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error('useCurrency must be used within a CurrencyProvider');
    }
    return context;
};

export const CurrencyProvider = ({ children }) => {
    const [selectedCurrency, setSelectedCurrency] = useState('EUR');

    // Load saved currency preference from localStorage
    useEffect(() => {
        const savedCurrency = localStorage.getItem('selectedCurrency');
        if (savedCurrency && EXCHANGE_RATES[savedCurrency]) {
            setSelectedCurrency(savedCurrency);
        }
    }, []);

    // Save currency preference to localStorage
    useEffect(() => {
        localStorage.setItem('selectedCurrency', selectedCurrency);
    }, [selectedCurrency]);

    // Convert price from EUR to selected currency
    const convertPrice = (eurPrice) => {
        const rate = EXCHANGE_RATES[selectedCurrency];
        return eurPrice * rate;
    };

    // Format price with currency symbol
    const formatPrice = (eurPrice) => {
        const convertedPrice = convertPrice(eurPrice);
        const currencyInfo = CURRENCY_INFO[selectedCurrency];

        // Format with appropriate decimal places
        const formattedAmount = convertedPrice.toFixed(currencyInfo.decimals);

        // For currencies with symbols that go before the amount
        if (['EUR', 'USD', 'AUD', 'CAD', 'NZD', 'CLP'].includes(selectedCurrency)) {
            return `${currencyInfo.symbol}${formattedAmount}`;
        }

        // For currencies with symbols that go after the amount
        return `${formattedAmount} ${currencyInfo.symbol}`;
    };

    // Get all available currencies
    const getAvailableCurrencies = () => {
        return Object.keys(CURRENCY_INFO).map(code => ({
            code,
            ...CURRENCY_INFO[code]
        }));
    };

    // Get current currency info
    const getCurrentCurrencyInfo = () => {
        return {
            code: selectedCurrency,
            ...CURRENCY_INFO[selectedCurrency]
        };
    };

    const value = {
        selectedCurrency,
        setSelectedCurrency,
        convertPrice,
        formatPrice,
        getAvailableCurrencies,
        getCurrentCurrencyInfo,
        exchangeRates: EXCHANGE_RATES,
        currencyInfo: CURRENCY_INFO
    };

    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
};

export default CurrencyContext; 